import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  NotificationsActive as CallIcon,
  Snooze as SnoozeIcon,
  ReceiptLong as ReceiptIcon,
  LockOpen as ReleaseIcon,
  MailOutline as LetterIcon,
  Gavel as AuctionIcon,
} from '@mui/icons-material';
import axios from 'axios';
import baseURL from '../../baseURL';
import { buildRemainderLettersFromItems, printRemainderLetters } from '../../utils/printUtility';

const SNOOZE_PRESETS = [
  { label: '1 day', days: 1 },
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
  { label: '15 days', days: 15 },
  { label: '1 month', days: 30 },
];

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

const iconBtnSx = {
  width: 32,
  height: 32,
  border: '1px solid #e2e8f0',
  bgcolor: '#fff',
};

const CallRemindersPanel = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [snoozeRow, setSnoozeRow] = useState(null);
  const [customUntil, setCustomUntil] = useState('');
  const [savingSnooze, setSavingSnooze] = useState(false);
  const [printingId, setPrintingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const res = await axios.get(`${baseURL}/pledges/dashboard/call-reminders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || '';
      if (/getting pledge|not found|404/i.test(msg) || err.response?.status === 404) {
        setItems([]);
      } else {
        setError(msg || 'Failed to load call reminders');
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const applySnooze = async (payload) => {
    if (!snoozeRow?._id) return;
    try {
      setSavingSnooze(true);
      setError('');
      const token = localStorage.getItem('token');
      await axios.patch(`${baseURL}/pledges/${snoozeRow._id}/call-reminder-snooze`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnoozeRow(null);
      setCustomUntil('');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to snooze');
    } finally {
      setSavingSnooze(false);
    }
  };

  const printRemainder = async (row) => {
    try {
      setPrintingId(row._id);
      setError('');
      setNotice('');
      const token = localStorage.getItem('token');
      const today = new Date();
      const finalDate = new Date(today);
      finalDate.setDate(finalDate.getDate() + 15);
      const iso = (d) => d.toISOString().slice(0, 10);
      const res = await axios.post(
        `${baseURL}/auction/reminder-letter/confirm`,
        { includeIds: [row._id], currentDate: iso(today), finalDate: iso(finalDate) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const confirmed = res.data?.data;
      const letters = buildRemainderLettersFromItems(confirmed?.items || [], {
        noticeDate: confirmed?.noticeDate || iso(today),
        finalDueDate: confirmed?.finalDate || iso(finalDate),
      });
      if (!letters.length) {
        setError('Could not build remainder letter for this ticket');
        return;
      }
      await printRemainderLetters(letters);
      setNotice(`Remainder letter sent to print for ticket ${row.ticketNo}`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to print remainder letter');
    } finally {
      setPrintingId(null);
    }
  };

  const dueCount = items.filter((r) => !r.snoozed).length;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2.5,
        border: '1px solid #e2e8f0',
        bgcolor: '#fff',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(180,83,9,0.08)',
              color: '#b45309',
            }}
          >
            <CallIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 750, color: '#0f172a' }}>
            Call after 1 year
          </Typography>
        </Stack>
        <Chip
          size="small"
          label={`${dueCount}`}
          sx={{ height: 22, fontWeight: 700, bgcolor: dueCount ? 'rgba(180,83,9,0.12)' : '#f1f5f9', color: dueCount ? '#9a3412' : '#475569' }}
        />
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 1.25, borderRadius: 2 }}>{error}</Alert> : null}
      {notice ? <Alert severity="success" sx={{ mb: 1.25, borderRadius: 2 }} onClose={() => setNotice('')}>{notice}</Alert> : null}

      {loading ? (
        <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b', bgcolor: '#f8fafc', py: 1 }}>Ticket</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b', bgcolor: '#f8fafc', py: 1 }}>Customer</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#64748b', bgcolor: '#f8fafc', py: 1, whiteSpace: 'nowrap' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, color: '#94a3b8', border: 0 }}>
                    No pledges older than one year
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row._id} hover sx={{ bgcolor: row.snoozed ? 'rgba(148,163,184,0.08)' : 'inherit' }}>
                    <TableCell sx={{ fontWeight: 800, color: '#8a6d2f', whiteSpace: 'nowrap', py: 1.05, borderColor: '#f1f5f9' }}>
                      {row.ticketNo || '—'}
                      {row.snoozed ? (
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b' }}>Till {formatDate(row.snoozeUntil)}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ py: 1.05, borderColor: '#f1f5f9' }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 650 }}>{row.customerName || '—'}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                        {row.mobileNo || row.customerCode || '—'}
                        {row.inAuctionCatalogue ? ' · auction' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.05, borderColor: '#f1f5f9' }}>
                      <Stack direction="row" spacing={0.4} justifyContent="flex-end">
                        <Tooltip title="Snooze">
                          <IconButton
                            size="small"
                            sx={iconBtnSx}
                            onClick={() => {
                              setSnoozeRow(row);
                              setCustomUntil('');
                            }}
                          >
                            <SnoozeIcon sx={{ fontSize: 17, color: '#b45309' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Receipt">
                          <IconButton
                            size="small"
                            sx={iconBtnSx}
                            onClick={() => navigate(`/pledge/create-receipt?ticket=${encodeURIComponent(row.ticketNo || '')}`)}
                          >
                            <ReceiptIcon sx={{ fontSize: 17, color: '#0f766e' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Release">
                          <IconButton
                            size="small"
                            sx={iconBtnSx}
                            onClick={() => navigate(`/release?ticket=${encodeURIComponent(row.ticketNo || '')}`)}
                          >
                            <ReleaseIcon sx={{ fontSize: 17, color: '#9a3412' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remainder letter">
                          <span>
                            <IconButton
                              size="small"
                              sx={iconBtnSx}
                              disabled={printingId === row._id}
                              onClick={() => printRemainder(row)}
                            >
                              {printingId === row._id ? (
                                <CircularProgress size={14} />
                              ) : (
                                <LetterIcon sx={{ fontSize: 17, color: '#1d4ed8' }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {row.inAuctionCatalogue ? (
                          <Tooltip title={row.catalogueNo ? `Auction catalogue ${row.catalogueNo}` : 'In auction catalogue'}>
                            <IconButton size="small" sx={iconBtnSx} onClick={() => navigate('/auction/catalogue')}>
                              <AuctionIcon sx={{ fontSize: 17, color: '#be123c' }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!snoozeRow} onClose={() => !savingSnooze && setSnoozeRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 750 }}>
          Snooze call · {snoozeRow?.ticketNo || ''}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#64748b', mb: 1.5 }}>
            Reminder stays in this section. “Call now” is hidden until the snooze date.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
            {SNOOZE_PRESETS.map((p) => (
              <Button key={p.days} size="small" variant="outlined" disabled={savingSnooze} onClick={() => applySnooze({ days: p.days })}>
                {p.label}
              </Button>
            ))}
          </Stack>
          <TextField
            type="date"
            size="small"
            fullWidth
            label="Custom date"
            InputLabelProps={{ shrink: true }}
            value={customUntil}
            onChange={(e) => setCustomUntil(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setSnoozeRow(null)} disabled={savingSnooze}>Cancel</Button>
          <Button
            variant="contained"
            disabled={savingSnooze || !customUntil}
            onClick={() => applySnooze({ until: customUntil })}
          >
            Snooze to date
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CallRemindersPanel;
